<template>
  <div class="teams-view-wrapper" data-cy="teams-view">
    <SButton
      type="button"
      variant="outline"
      size="icon"
      class="close-button rounded-full"
      aria-label="close"
      data-cy="teams-close-btn"
      @click.stop="close"
    >
      <SvgIcon
        :iconContent="closeIcon"
        iconAlt="close"
        iconStyle="width: 20px; height: auto;"
        class="close-icon"
      />
    </SButton>
    <div class="teams-container w-full h-full d-flex fl-j-c fl-a-c">
      <div :class="isLoading ? 'overlay w-full h-full' : ''"></div>

      <div
        class="left w-1/2 h-full d-flex fl-col fl-s-b"
        v-if="teamsArray.length"
      >
        <div class="swiper-wrapper fl-a-c">
          <swiper
            :modules="modules"
            :slides-per-view="6"
            :space-between="10"
            :centered-slides="false"
            :freeMode="true"
            :navigation="true"
            virtual
            ref="swiper"
            @swiper="setSwiperRef"
          >
            <swiper-slide
              v-for="(team, index) in teamsArray"
              :key="index"
              :virtualIndex="index"
              class="d-flex fl-s-b fl-a-c"
            >
              <div
                :class="`team d-flex fl-col fl-s-b ${
                  currentTeam === index ? 'selected' : ''
                }`"
                :data-cy="`teams-team-card-${index}`"
                @click.stop="selectTeam(index)"
              >
                <h3>{{ `${$t("teams.team")} ${index + 1}` }}</h3>
                <p class="f-small">
                  {{
                    `${team.roomPlayers} ${
                      team.roomPlayers > 1
                        ? $t("teams.players")
                        : $t("teams.player")
                    }`
                  }}
                </p>
                <p
                  class="f-small"
                  :style="{ color: team.paired ? '#00975F' : '#FF5630' }"
                  data-cy="teams-paired-badge"
                >
                  {{ team.paired ? $t("teams.paired") : $t("teams.unpaired") }}
                </p>
              </div>
              <SSeparator orientation="vertical" />
            </swiper-slide>
          </swiper>
        </div>
        <div class="bottom d-flex fl-col fl-s-b fl-a-c">
          <div class="w-full">
            <p class="pb-2.5 f-large">
              {{
                `${teamsArray[currentTeam].roomPlayers} ${
                  teamsArray[currentTeam].roomPlayers > 1
                    ? $t("teams.players")
                    : $t("teams.player")
                }`
              }}
            </p>
            <div>
              <div class="input-label w-full d-flex fl-s-b fl-a-c">
                <label>{{ $t("teams.name") }}</label
                ><span>{{ $t("custom-fields.mandatory").toUpperCase() }}</span>
              </div>
              <SInput
                v-model="form.name"
                :placeholder="$t('teams.placeholder')"
                class="mb-4"
                data-cy="teams-name-input"
              />

              <div class="input-label w-full d-flex fl-s-b fl-a-c">
                <label>{{ $t("teams.level") }}</label>
              </div>
              <SSelect v-model="form.level">
                <SSelectTrigger class="w-full" data-cy="teams-level-select">
                  <SSelectValue :placeholder="$t('teams.select-level')" />
                </SSelectTrigger>
                <SSelectContent>
                  <SSelectItem
                    v-for="item in levelOptions"
                    :key="item.value"
                    :value="item.value"
                  >
                    {{ item.label }}
                  </SSelectItem>
                </SSelectContent>
              </SSelect>
            </div>
          </div>
          <div class="buttons w-full d-flex fl-col fl-a-c gap-2">
            <SButton
              v-if="!teamsArray[currentTeam].paired"
              type="button"
              size="lg"
              class="w-full"
              :disabled="!form.name"
              data-cy="teams-scan-and-pair-btn"
              @click="scanAndPair()"
            >
              {{ $t("button.pair-team") }}
            </SButton>
            <SButton
              type="button"
              size="lg"
              class="w-full"
              :disabled="!teamsArray[currentTeam].paired"
              data-cy="teams-proceed-btn"
              @click="proceed"
            >
              {{ $t("button.save") }}
            </SButton>
          </div>
        </div>
      </div>
      <div class="right w-1/2 h-full">
        <div class="content d-flex fl-j-c fl-a-c fl-col f-large">
          <p v-if="isLoading">{{ $t("teams.pairing") }}</p>
          <p v-else>
            {{
              teamsArray[currentTeam]?.paired
                ? $t("teams.paired-successfully")
                : $t("teams.get-ready")
            }}
          </p>
          <div class="card-icon d-flex fl-col fl-s-b fl-a-c">
            <SvgIcon
              :iconContent="cardsIcon"
              iconAlt="card-icon"
              iconStyle="width: 200px; height: auto;"
            />
            <SvgIcon
              v-if="isLoading"
              :iconContent="arrowDownIcon"
              iconAlt="arrow-down"
              iconStyle="width: 152px; height: auto;"
            />
            <SvgIcon
              v-if="teamsArray[currentTeam]?.paired"
              :iconContent="successIcon"
              iconAlt="info"
              iconStyle="width: 80px; height: auto;"
              class="success-icon"
            />
          </div>
          <SButton
            v-if="teamsArray[currentTeam]?.paired"
            type="button"
            variant="ghost"
            class="unpair-button"
            data-cy="teams-unpair-btn"
            @click="unpair"
          >
            {{ $t("button.unpair") }}
          </SButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  onMounted,
  onUnmounted,
  ref,
  watch,
} from "vue";
import { useI18n } from "vue-i18n";
import { storeToRefs } from "pinia";
import { useOrderStore } from "@smeetz/order-store";
import { OrderToken } from "@smeetz/order-common";
import { useOrderMetaStore } from "@/stores/order-meta.store";
import { BookingStep } from "@/constants/booking";
import { ITeam, TeamLevel } from "@/interfaces/teams/teams";
import {
  deleteTeam,
  getOrderTeams,
  patchPairTeam,
  postPairTeam,
} from "@/api/teams/teams";
import {
  buildOrderTeams,
  getOrderTeamSettings,
  mergeOrderTeams,
  resolveTeamLines,
} from "@/utils/helpers/order-teams";
import { getHttpStatus } from "@/utils/helpers/not-synced-retry";
import { loadTokens, scanByToken } from "@/utils/vue-helpers/orders.helper";
import { logOrderActionError } from "@/utils/vue-helpers/order-action-errors.helper";
import { saveOrderForSync } from "@/db/orders";
import { attachedVueElement, detachVueElement } from "@/utils/helpers/scanner";
import ScannerModule from "@/store/modules/scanner";
import AppModule from "@/store/modules/app";
import UserModule from "@/store/modules/user";
import POSConfigModule from "@/store/modules/pos-config";
import NotificationService from "@/services/notification.service";
import { LogLevel, LogService } from "@/services/logging";
import cardsIcon from "@/assets/images/svg/cards.svg";
import closeIcon from "@/assets/images/svg/close.svg";
import successIcon from "@/assets/images/svg/round-check.svg";
import arrowDownIcon from "@/assets/images/svg/arrow-down.svg";
import { Virtual, FreeMode, Navigation } from "swiper";
import { Swiper, SwiperSlide } from "swiper/vue";
import "swiper/scss"; // core Swiper
import "swiper/scss/navigation"; // Navigation module
import "swiper/scss/pagination"; // Pagination module

import {
  SButton,
  SSeparator,
  SInput,
  SSelect,
  SSelectContent,
  SSelectItem,
  SSelectTrigger,
  SSelectValue,
} from "@smeetz/ui-core";

/**
 * Groups whose tills have no NFC reader: the pairing step skips the scan and
 * pairs with a fixed tag. 16342 is the historical manual-pairing group; 16632
 * is the E2E test group, accepted outside production only.
 */
const MANUAL_PAIRING_GROUP_ID = 16342;
const E2E_TEST_GROUP_ID = 16632;
const MANUAL_PAIRING_TAG = "ABCDEFGH";

let swiper: any = null;
export default defineComponent({
  name: "Teams",
  components: {
    Swiper,
    SwiperSlide,
    SButton,
    SSeparator,
    SInput,
    SSelect,
    SSelectContent,
    SSelectItem,
    SSelectTrigger,
    SSelectValue,
  },
  setup() {
    const { t } = useI18n({});
    const orderStore = useOrderStore();
    const { selectedOrder, allOrderLines } = storeToRefs(orderStore);
    const orderMetaStore = useOrderMetaStore();

    const teamsArray = ref<ITeam[]>([]);
    const currentTeam = ref(0);
    const isLoading = ref(false);
    const nfcTag = ref("");
    const form = ref({
      name: "",
      level: TeamLevel.MEDIUM,
    });

    const levelOptions = [
      { label: t("teams.level-easy"), value: TeamLevel.EASY },
      { label: t("teams.level-medium"), value: TeamLevel.MEDIUM },
      { label: t("teams.level-hard"), value: TeamLevel.HARD },
    ];

    function setSwiperRef(swp: any) {
      swiper = swp;
    }

    const isManualPairing = computed(() => {
      const groupId = POSConfigModule.groupId;
      return (
        groupId === MANUAL_PAIRING_GROUP_ID ||
        (import.meta.env.VITE_ENV !== "prod" && groupId === E2E_TEST_GROUP_ID)
      );
    });

    const scannerTog = computed(() => ScannerModule.tog);
    function waitForNfcTag(): Promise<string> {
      return new Promise((resolve) => {
        const stop = watch(
          scannerTog,
          () => {
            LogService.info(
              `Teams: scanner watcher triggered, value = ${ScannerModule.value}`
            );
            if (ScannerModule.value) {
              stop();
              resolve(ScannerModule.value);
            }
          },
          { deep: true }
        );
      });
    }

    function close() {
      orderMetaStore.setStep(BookingStep.CompleteOrder);
    }

    function selectTeam(index: number) {
      currentTeam.value = index;
      form.value.name = teamsArray.value[index].name;
      form.value.level = teamsArray.value[index].teamLevel || TeamLevel.MEDIUM;
    }

    /** Name or level edited in the form after the team was paired. */
    function hasPendingEdits(team: ITeam): boolean {
      return (
        form.value.name.trim() !== team.name ||
        form.value.level !== team.teamLevel
      );
    }

    /**
     * Moves to the next team, or closes the view after the last one. A paired
     * team whose name or level changed in the form is updated first, so an
     * edit after pairing does not need a new card scan.
     */
    async function proceed() {
      if (isLoading.value) return;
      const team = teamsArray.value[currentTeam.value];
      if (team?.paired && team.uuid && hasPendingEdits(team)) {
        isLoading.value = true;
        try {
          const saved = await submitTeam(team, {
            name: form.value.name.trim(),
            teamLevel: form.value.level,
          });
          if (!saved) return;
        } finally {
          isLoading.value = false;
        }
      }
      if (currentTeam.value === teamsArray.value.length - 1) {
        close();
        return;
      }
      selectTeam(currentTeam.value + 1);
    }

    function unpair() {
      teamsArray.value[currentTeam.value].paired = false;
    }

    function notifyOrderNotSynced(action: string, orderId: string) {
      LogService.info(
        `Warning ${action}: ORDER_NOT_SYNCED`,
        { orderId },
        LogLevel.Warning
      );
      NotificationService.showCustomError(t("teams.order-not-synced"));
    }

    /**
     * Scans the paired team's tickets locally (one scan per token, offline
     * safe) and re-queues the order once so the backend syncs the scans.
     */
    async function scanTeamTokens(tokenValues: string[]) {
      const order = selectedOrder.value;
      if (!order) return;
      const scannedAt = new Map(
        (order.tokens ?? []).map((token: OrderToken) => [
          token.token,
          token.scannedAt,
        ])
      );
      let scannedAny = false;
      for (const token of tokenValues) {
        if (scannedAt.get(token)) continue;
        try {
          await scanByToken(token, UserModule.userId ?? undefined);
          scannedAny = true;
        } catch (error) {
          logOrderActionError(
            "scanning team ticket",
            { orderId: order.id },
            error
          );
        }
      }
      if (scannedAny) {
        await saveOrderForSync(selectedOrder.value);
      }
    }

    /**
     * Creates the team on the backend, or updates it when it already has a
     * uuid, and applies the given changes to the local team on success. A
     * business refusal (duplicate name) and a not-synced order each show their
     * own message; anything else is journaled with a general error.
     *
     * @returns whether the backend accepted the team
     */
    async function submitTeam(
      team: ITeam,
      changes: Partial<Pick<ITeam, "name" | "teamLevel" | "nfcTag">>
    ): Promise<boolean> {
      const order = selectedOrder.value;
      if (!order?.id) return false;
      const candidate: ITeam = { ...team, ...changes };

      try {
        const res = candidate.uuid
          ? await patchPairTeam(candidate, order.id)
          : await postPairTeam(candidate, order.id);
        const saved = res.data?.teamOfParticipants;
        if (res.status !== "success" || !saved) {
          const message = res.data?.errors?.[0] ?? "";
          LogService.info(
            `Warning saving team: ${message || "REFUSED"}`,
            { orderId: order.id, teamUuid: candidate.uuid, response: res },
            LogLevel.Warning
          );
          NotificationService.showCustomError(
            message.includes("The team with name")
              ? t("teams.name-taken")
              : message || t("error.general-error")
          );
          return false;
        }
        Object.assign(team, changes, { uuid: saved.uuid });
        LogService.info(`Saved team ${team.uuid} on order ${order.reference}`, {
          orderId: order.id,
          roomPlayers: team.roomPlayers,
        });
        return true;
      } catch (error) {
        if (getHttpStatus(error) === 409) {
          notifyOrderNotSynced("saving team", order.id);
          return false;
        }
        logOrderActionError(
          "saving team",
          { orderId: order.id, teamUuid: candidate.uuid },
          error
        );
        NotificationService.showGeneralResponseError();
        return false;
      }
    }

    async function pair() {
      const order = selectedOrder.value;
      const team = teamsArray.value[currentTeam.value];
      if (!order?.id || !team) return;

      // The reader may add spaces and lowercase hex digits.
      const formattedNfcTag = nfcTag.value.replace(/\s+/g, "").toUpperCase();
      if (!isManualPairing.value && !/^[0-9A-F]+$/i.test(formattedNfcTag)) {
        LogService.info(
          `Warning pairing team: INVALID_NFC_TAG`,
          { orderId: order.id, nfcTag: formattedNfcTag },
          LogLevel.Warning
        );
        NotificationService.showCustomError(
          t("teams.invalid-tag").replace("xxxxxxx", formattedNfcTag)
        );
        return;
      }

      const saved = await submitTeam(team, {
        name: form.value.name.trim(),
        teamLevel: form.value.level,
        nfcTag: formattedNfcTag,
      });
      if (saved) {
        team.paired = true;
        await scanTeamTokens(team.tokens);
      }
    }

    async function scanAndPair() {
      if (isLoading.value) return;
      if (!isManualPairing.value && !form.value.name.trim()) {
        LogService.info(
          "Warning pairing team: EMPTY_NAME",
          {},
          LogLevel.Warning
        );
        return;
      }
      LogService.info("User clicked to scan and pair a team");
      isLoading.value = true;
      try {
        nfcTag.value = isManualPairing.value
          ? MANUAL_PAIRING_TAG
          : await waitForNfcTag();
        LogService.info(`Teams: received NFC tag: ${nfcTag.value}`);
        await pair();
      } finally {
        isLoading.value = false;
      }
    }

    /**
     * Builds the order's suggested teams from its tokens, merges them with
     * the teams already paired on the backend and cancels the backend teams
     * whose tickets are gone. Leaves the view when the order is not synced
     * yet or needs no team.
     */
    async function loadTeams() {
      const order = selectedOrder.value;
      if (!order?.id) {
        close();
        return;
      }
      AppModule.updatedIsLoading(true);
      try {
        const tokens = order.tokens?.length ? order.tokens : await loadTokens();
        const resolvedLines = await resolveTeamLines(allOrderLines.value);
        const settings = getOrderTeamSettings(resolvedLines);
        const suggested = await buildOrderTeams(
          tokens,
          resolvedLines,
          settings
        );
        const fetched = await getOrderTeams(order.id);
        const { teams, toCancel } = mergeOrderTeams(suggested, fetched);

        for (const team of toCancel) {
          try {
            await deleteTeam(String(team.uuid), order.id);
          } catch (error) {
            logOrderActionError(
              "cancelling extra team",
              { orderId: order.id, teamUuid: team.uuid },
              error
            );
          }
        }

        if (!teams.length) {
          LogService.info(
            "Warning loading teams: NO_TEAM_REQUIRED",
            { orderId: order.id },
            LogLevel.Warning
          );
          close();
          return;
        }
        teamsArray.value = teams;
        selectTeam(0);
      } catch (error) {
        if (getHttpStatus(error) === 409) {
          notifyOrderNotSynced("loading teams", order.id);
        } else {
          logOrderActionError("loading teams", { orderId: order.id }, error);
          NotificationService.showGeneralResponseError();
        }
        close();
      } finally {
        AppModule.updatedIsLoading(false);
      }
    }

    onMounted(() => {
      LogService.info(`Scanner: going to attach scanner to Teams vue`);
      attachedVueElement();
      loadTeams();
    });
    onUnmounted(() => {
      detachVueElement();
    });

    return {
      t,
      teamsArray,
      currentTeam,
      isLoading,
      form,
      levelOptions,
      cardsIcon,
      closeIcon,
      successIcon,
      arrowDownIcon,
      modules: [Navigation, Virtual, FreeMode],
      setSwiperRef,
      selectTeam,
      scanAndPair,
      proceed,
      unpair,
      close,
    };
  },
});
</script>

<style lang="scss">
@use "@/styles/variables.scss" as *;
.teams-view-wrapper {
  position: absolute;
  width: calc(100% - 40px);
  height: calc(100% - 40px);
  z-index: 999;
  background-color: $color-light-grey4;
  font-size: 16px;

  .teams-container {
    position: relative;
  }
  .overlay {
    position: absolute;
    z-index: 1;
    background-color: rgba($color: #000000, $alpha: 0.4);
  }

  .close-button {
    position: absolute;
    right: 20px;
    top: 20px;
    width: 30px;
    height: 30px;
    background-color: #e5e5e5;
    z-index: 10;
  }

  .left {
    border-right: 2px solid #c3c3c3;
    .input-label {
      padding-bottom: 5px;
      span {
        color: #c9c9c9;
      }
    }
    .bottom {
      flex-grow: 1;
      padding: 20px;
      border-top: 2px solid #c3c3c3;
    }
    .buttons {
      gap: 10px;
      .button {
        margin: 0;
      }
    }
  }

  .right {
    position: relative;
    .content {
      position: absolute;
      top: 50px;
      left: 50px;
      width: calc(100% - 100px);
      height: calc(100% - 100px);
      z-index: 10;
      gap: 50px;
      flex-direction: column;
      background-color: $color-light-grey4;
    }
    .card-icon {
      position: relative;
    }
    .success-icon {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
    .unpair-button {
      text-decoration: underline;
    }
  }

  .f-small {
    font-size: 12px;
  }
  .f-large {
    font-size: 32px;
  }
  .swiper-wrapper {
    background-color: $color-light-grey4;
    height: 20%;
    position: sticky;
    z-index: 11;
    .team {
      height: 56px;
      opacity: 0.4;
      cursor: pointer;
    }
    .selected {
      opacity: 1;
    }
    .swiper {
      width: 100%;
      padding-left: 50px;
      .swiper-button-prev,
      .swiper-button-next {
        opacity: 1;
        background-color: $color-light-grey4;
        z-index: 12;
      }
      .swiper-button-prev::after,
      .swiper-button-next::after {
        font-size: 25px;
        font-weight: 900;
        color: $color-black-2;
      }
      .swiper-button-prev {
        left: 0;
        box-shadow: 10px 0 15px -2px $color-light-grey4;
      }
      .swiper-button-next {
        right: 0;
        box-shadow: -10px 0 15px -2px $color-light-grey4;
      }
    }
  }
}
</style>
